import {
  createContext,
  useContext,
  useId,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react'
import { cn } from '../lib/cn'

/**
 * Примитивы интерфейса.
 *
 * Правило шаблона: синим окрашено только главное действие на экране. Поэтому вариант
 * primary на странице ровно один, всё остальное — ghost или quiet. Если синих кнопок
 * стало две, значит экран не решил, чего хочет от человека.
 *
 * Второе правило — про доступность. Radix в проект не завели, поэтому подпись поля,
 * связь подсказки с полем и видимое состояние фокуса делает Field и кнопки здесь,
 * а не каждая страница по-своему. Подпись, набранная просто span-ом рядом с input,
 * для скринридера не существует: он читает «поле ввода» и всё.
 */

/**
 * Связь подписи с полем. Field выдаёт идентификаторы, Input и Select их подхватывают —
 * страницам не приходится придумывать id руками, и ни одно поле не остаётся без подписи.
 */
const FieldContext = createContext<{ id?: string; describedBy?: string; invalid?: boolean }>({})

type ButtonVariant = 'primary' | 'quiet' | 'ghost' | 'danger'
type ButtonSize = 'sm' | 'md' | 'lg'

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-accent-fg hover:opacity-90 active:opacity-100',
  quiet: 'bg-surface-3 text-fg hover:bg-line/60',
  ghost: 'border border-line bg-surface text-fg hover:bg-surface-2',
  danger: 'border border-state-busy/40 bg-surface text-state-busy hover:bg-state-busy/10',
}

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-4 text-[15px]',
  lg: 'h-12 px-6 text-base',
}

export function Button({
  variant = 'ghost',
  size = 'md',
  className,
  loading,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
}) {
  return (
    <button
      {...rest}
      disabled={rest.disabled || loading}
      className={cn(
        'inline-flex select-none items-center justify-center gap-2 rounded-lg font-medium transition',
        // Кольцо в полный цвет и с отступом: полупрозрачное на синей кнопке
        // не видно вовсе, а именно на ней фокус чаще всего и оказывается.
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
    >
      {loading && <Spinner />}
      {children}
    </button>
  )
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent',
        className,
      )}
      aria-hidden
    />
  )
}

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  const field = useContext(FieldContext)
  return (
    <input
      {...rest}
      id={rest.id ?? field.id}
      aria-describedby={rest['aria-describedby'] ?? field.describedBy}
      aria-invalid={rest['aria-invalid'] ?? (field.invalid || undefined)}
      className={cn('field', className)}
    />
  )
}

export function Select({
  className,
  children,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { children: ReactNode }) {
  const field = useContext(FieldContext)
  return (
    <select
      {...rest}
      id={rest.id ?? field.id}
      aria-describedby={rest['aria-describedby'] ?? field.describedBy}
      aria-invalid={rest['aria-invalid'] ?? (field.invalid || undefined)}
      className={cn('field', className)}
    >
      {children}
    </select>
  )
}

/**
 * Поле с подписью, подсказкой и ошибкой.
 *
 * Подпись — настоящий label, привязанный к полю через for/id: нажатие по ней ставит
 * фокус в поле, а скринридер называет поле, а не отчитывается «поле ввода». Подсказка
 * и текст ошибки уходят в aria-describedby, ошибка ещё и поднимает aria-invalid —
 * иначе о ней узнают только глазами.
 *
 * `group` — для случая, когда внутри не одно поле, а набор переключателей («Что отменяем»,
 * «Что делать с будущими бронями»). Там label запрещён: нажатие по подписи включило бы
 * первый переключатель. Вместо него role="group" с той же подписью.
 */
export function Field({
  label,
  hint,
  error,
  children,
  className,
  group,
  id: explicitId,
}: {
  label?: string
  hint?: string
  error?: string
  children: ReactNode
  className?: string
  group?: boolean
  /** Для полей, которые верстаются вручную (код из письма) и не берут id из контекста. */
  id?: string
}) {
  const base = useId()
  const id = explicitId ?? `${base}-control`
  const labelId = `${base}-label`
  const hintId = `${base}-hint`
  const describedBy = error || hint ? hintId : undefined

  const tail = error ? (
    <p id={hintId} className="mt-1.5 text-sm text-state-busy">
      {error}
    </p>
  ) : hint ? (
    <p id={hintId} className="mt-1.5 hint">
      {hint}
    </p>
  ) : null

  if (group) {
    return (
      <div className={className} role="group" aria-labelledby={label ? labelId : undefined}>
        {label && (
          <span id={labelId} className="label">
            {label}
          </span>
        )}
        {children}
        {tail}
      </div>
    )
  }

  return (
    <FieldContext.Provider value={{ id, describedBy, invalid: Boolean(error) }}>
      <div className={className}>
        {label && (
          <label htmlFor={id} className="label">
            {label}
          </label>
        )}
        {children}
        {tail}
      </div>
    </FieldContext.Provider>
  )
}

/** Переключатель прав: включён — синий, потому что это действие, а не состояние комнаты. */
export function Switch({
  checked,
  onChange,
  label,
  disabled,
  title,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label?: string
  disabled?: boolean
  title?: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      title={title}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
        checked ? 'border-accent bg-accent' : 'border-line bg-surface-3',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <span
        className={cn(
          'h-4 w-4 rounded-full bg-surface transition',
          checked ? 'translate-x-6' : 'translate-x-1',
          checked && 'bg-accent-fg',
        )}
      />
    </button>
  )
}

/** Группа переключателей одного выбора: фильтры «Все / Я организую / Меня позвали». */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  className,
  label,
}: {
  value: T
  options: Array<{ value: T; label: string }>
  onChange: (next: T) => void
  className?: string
  /** Чем эта группа фильтрует. Видимой подписи у неё нет, а скринридеру она нужна. */
  label?: string
}) {
  return (
    <div
      role="group"
      aria-label={label}
      /*
        max-w-full и прокрутка обязательны: пять фильтров сотрудников («Все / Работают /
        Ожидают / Заблокированы / Уволены») занимают 472 точки, а телефон даёт 390.
        inline-flex без прокрутки просто вылезал за экран — последний фильтр был
        недостижим ни нажатием, ни прокруткой страницы. shrink-0 на кнопках нужен там же:
        без него они сжимаются в нечитаемые столбики букв вместо того, чтобы прокручиваться.
      */
      className={cn(
        'inline-flex max-w-full overflow-x-auto rounded-lg border border-line bg-surface-2 p-1',
        className,
      )}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          aria-pressed={value === option.value}
          className={cn(
            'shrink-0 rounded-md px-3 py-1.5 text-sm font-medium transition',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
            value === option.value ? 'bg-surface text-fg shadow-sm' : 'text-muted hover:text-fg',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

export function Badge({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode
  tone?: 'neutral' | 'accent' | 'free' | 'soon' | 'busy'
  className?: string
}) {
  const tones: Record<string, string> = {
    neutral: 'border-line bg-surface-2 text-muted',
    accent: 'border-accent/30 bg-accent-soft text-accent',
    free: 'border-state-free/30 bg-state-free/10 text-state-free',
    soon: 'border-state-soon/30 bg-state-soon/10 text-state-soon',
    busy: 'border-state-busy/30 bg-state-busy/10 text-state-busy',
  }
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

export function PageTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
      <h1 className="text-2xl font-semibold tracking-tight">{children}</h1>
      {action}
    </div>
  )
}

/** Пустое состояние: всегда объясняет, что делать дальше, а не просто «ничего нет». */
export function Empty({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="card flex flex-col items-center gap-3 px-6 py-12 text-center">
      <p className="text-base font-medium">{title}</p>
      {hint && <p className="hint max-w-md">{hint}</p>}
      {action}
    </div>
  )
}

export function Loading({ text = 'Загружаем…' }: { text?: string }) {
  return (
    <div className="flex items-center gap-2 px-1 py-8 text-muted">
      <Spinner /> {text}
    </div>
  )
}
